import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import purchaseRequestService from '../../services/purchaseRequestService';
import toast from 'react-hot-toast';
import { Download, FileText, ExternalLink, ArrowLeft, User, Building, Banknote, Calendar, Clock, CheckCircle, AlertCircle, Package, CreditCard, Edit, Save, X, Upload } from 'lucide-react';
import PurchaseReceipt from './PurchaseReceipt';

const PurchaseRequestDetail = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceipt, setShowReceipt] = useState(false);
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  const [editForm, setEditForm] = useState({});
  const [newDocument, setNewDocument] = useState(null);

  useEffect(() => {
    fetchRequestDetails();
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      const response = await purchaseRequestService.getRequestById(requestId);
      const requestData = response.data.data.request;
      setRequest(requestData);
      setEditForm({
        item_service_requested: requestData.item_service_requested,
        justification: requestData.justification,
        amount: requestData.amount,
        vendor_name: requestData.vendor_name,
        priority: requestData.priority
      });
    } catch (error) {
      console.error('Fetch request details error:', error);
      toast.error('Failed to fetch request details');
      navigate('/purchase-requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const formData = new FormData();
      formData.append('item_service_requested', editForm.item_service_requested);
      formData.append('justification', editForm.justification);
      formData.append('amount', editForm.amount);
      formData.append('vendor_name', editForm.vendor_name);
      formData.append('priority', editForm.priority);
      if (newDocument) {
        formData.append('supporting_document', newDocument);
      }
      
      await purchaseRequestService.updateRequest(requestId, formData);
      await purchaseRequestService.resubmitRequest(requestId);
      toast.success('Request updated and resubmitted successfully');
      navigate('/purchase-requests/create');
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleDownloadReceipt = () => {
    setShowReceipt(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-100 text-blue-700 border-blue-200',
      pending_approval: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      pending_procurement_review: 'bg-purple-100 text-purple-700 border-purple-200',
      pending_finance_approval: 'bg-orange-100 text-orange-700 border-orange-200',
      payment_in_progress: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      awaiting_delivery_confirmation: 'bg-teal-100 text-teal-700 border-teal-200',
      completed: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-700 border-red-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      medium: 'bg-blue-100 text-blue-700 border-blue-300',
      low: 'bg-gray-100 text-gray-700 border-gray-300'
    };
    return colors[priority] || colors.low;
  };

  const formatCurrency = (amount) => {
    return '₦' + new Intl.NumberFormat('en-NG', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Request not found</h3>
        <p className="text-gray-500 mt-2">The requested purchase request could not be found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{request.request_id}</h1>
                <p className="text-sm text-gray-600 mt-0.5">Purchase Request Details</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              {request.status === 'rejected' && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <Edit className="h-4 w-4" />
                  <span className="font-medium">Edit Request</span>
                </button>
              )}
              {isEditing && (
                <>
                  <button
                    onClick={handleSaveEdit}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                  >
                    <Save className="h-4 w-4" />
                    <span className="font-medium">Save & Resubmit</span>
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-sm"
                  >
                    <X className="h-4 w-4" />
                    <span className="font-medium">Cancel</span>
                  </button>
                </>
              )}
              {!isEditing && (
                <button
                  onClick={handleDownloadReceipt}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <Download className="h-4 w-4" />
                  <span className="font-medium">Download Receipt</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Status Banner */}
        <div className={`rounded-xl border-2 p-6 ${getStatusColor(request.status)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-lg">
                {request.status === 'completed' ? (
                  <CheckCircle className="h-6 w-6" />
                ) : request.status === 'rejected' ? (
                  <AlertCircle className="h-6 w-6" />
                ) : (
                  <Clock className="h-6 w-6" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium opacity-80">Current Status</p>
                <p className="text-lg font-bold capitalize">{request.status.replace(/_/g, ' ')}</p>
              </div>
            </div>
            <span className={`px-4 py-2 rounded-lg border-2 text-sm font-bold uppercase tracking-wide ${getPriorityColor(request.priority)}`}>
              {request.priority} Priority
            </span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Request Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Request Information
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Item/Service Requested
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.item_service_requested}
                      onChange={(e) => setEditForm({...editForm, item_service_requested: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="text-base text-gray-900 leading-relaxed">
                      {request.item_service_requested}
                    </p>
                  )}
                </div>

                {(request.justification || isEditing) && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Justification
                    </label>
                    {isEditing ? (
                      <textarea
                        value={editForm.justification}
                        onChange={(e) => setEditForm({...editForm, justification: e.target.value})}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                        {request.justification}
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Banknote className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Amount
                      </label>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editForm.amount}
                          onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                          className="w-full px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(request.amount)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Created On
                      </label>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(request.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Vendor Information
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Vendor Name
                  </label>
                  <p className="text-base font-medium text-gray-900">{request.vendor_name}</p>
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Bank Details
                  </label>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                      {request.vendor_bank_details}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Supporting Document */}
            {(request.cdn_url || request.supporting_document_path || isEditing) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Supporting Document
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  {(request.cdn_url || request.supporting_document_path) && (
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-white rounded-lg shadow-sm">
                          <FileText className="h-8 w-8 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Current Document</p>
                          <p className="text-xs text-gray-600 mt-0.5">Click to view or download</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={request.cdn_url || request.supporting_document_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                        >
                          <ExternalLink className="h-4 w-4" />
                          View
                        </a>
                        <a
                          href={request.cdn_url || request.supporting_document_path}
                          download
                          className="inline-flex items-center gap-2 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {isEditing && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {request.cdn_url || request.supporting_document_path ? 'Replace Document' : 'Upload Document'}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          onChange={(e) => setNewDocument(e.target.files[0])}
                          className="flex-1 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {newDocument && (
                          <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                            <CheckCircle className="h-4 w-4" />
                            {newDocument.name}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Requester Info */}
          <div className="space-y-6">
            {/* Requester Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Requester
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {request.requester_name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-base font-bold text-gray-900">{request.requester_name}</p>
                    <p className="text-sm text-gray-600">{request.requester_email}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-100 rounded-lg">
                      <Building className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Department
                      </label>
                      <p className="text-sm font-medium text-gray-900">{request.department}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
              <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 opacity-90">Request Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-90">Request ID</span>
                  <span className="font-bold">{request.request_id}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-90">Priority</span>
                  <span className="font-bold uppercase">{request.priority}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-90">Status</span>
                  <span className="font-bold capitalize">{request.status.replace(/_/g, ' ')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        {request.notes && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
              <h3 className="text-lg font-bold text-white">Comments & Notes</h3>
            </div>
            <div className="p-6">
              <div className="bg-orange-50 rounded-lg p-4 border-2 border-orange-200">
                <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                  {request.notes}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {showReceipt && (
        <PurchaseReceipt 
          request={request} 
          onClose={() => setShowReceipt(false)} 
        />
      )}
    </div>
  );
};

export default PurchaseRequestDetail;